import { isFedRamp } from '@atlassian/atl-context';

import { useSessionData } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition';

import { useReverseTrialEligible } from './useReverseTrialEligible';
import { ReverseTrialCohort } from './__types__/InstanceIsInReverseTrialQuery';
import { useTrialDetails } from './useTrialDetails';

type HookType = {
	isPremiumOnboardingEnabled?: boolean;
	shouldCheckIsInTrial?: boolean;
};

export const useIsPremiumOnboardingFeatureDiscoveryEnabled = ({
	isPremiumOnboardingEnabled = true,
	shouldCheckIsInTrial = true,
}: HookType = {}) => {
	const { edition, isLoggedIn, isLicensed, userId } = useSessionData();
	const isFedrampEnv = isFedRamp();

	const isUserAndInstanceEligible =
		isPremiumOnboardingEnabled &&
		edition === ConfluenceEdition.PREMIUM &&
		isLoggedIn &&
		isLicensed &&
		Boolean(userId) &&
		// FedRamp shouldn't have any premium trial customer but we are excluding it just in case
		!isFedrampEnv;

	const {
		loading: reverseTrialLoading,
		error: reverseTrialError,
		reverseTrialCohort,
	} = useReverseTrialEligible({ skip: !isUserAndInstanceEligible });

	const { trialData, trialDetailsError, trialDetailsLoading, trialDaysLeft, isInstanceInTrial } =
		useTrialDetails({
			skip: !shouldCheckIsInTrial || !isUserAndInstanceEligible,
		});

	const isReverseTrialEnrolled = reverseTrialCohort === ReverseTrialCohort.ENROLLED; // checks if it is RT instance
	const isPTOEligible = isUserAndInstanceEligible && !reverseTrialLoading && !reverseTrialError;
	const isPTORTEligible = isPTOEligible && isReverseTrialEnrolled;

	const isPremiumOnboardingFeatureDiscoveryEnabled =
		isPTOEligible &&
		(!shouldCheckIsInTrial || (!trialDetailsLoading && !trialDetailsError && isInstanceInTrial));

	const isPTORTEnabled = isPTORTEligible && isPremiumOnboardingFeatureDiscoveryEnabled;

	return {
		loading: trialDetailsLoading || reverseTrialLoading,
		inTrialError: trialDetailsError,
		reverseTrialError,
		isPremiumOnboardingFeatureDiscoveryEnabled,
		isPTORTEnabled,
		trialDaysLeft,
		trialEndDate: trialData?.license?.trialEndDate,
	};
};
